import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import axAPI from "api/axios-instance";
import { DraftImage, MaterialBlock, MedicineMaterialsResponse } from "./types";
import {
  createDraftImage,
  createNewBlock,
  getFilledDrafts,
  groupMaterials,
} from "./utils";

export const useMedicineMaterials = () => {
  const [blocks, setBlocks] = useState<MaterialBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingBlockKey, setSavingBlockKey] = useState<string>();
  const [removingIds, setRemovingIds] = useState<number[]>([]);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axAPI.get<MedicineMaterialsResponse>(
        "/cms/medicine/materials"
      );
      setBlocks(groupMaterials(data.materials || []));
    } catch (error: any) {
      message.error(error?.message || "Не удалось загрузить материалы");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const addBlock = () => {
    setBlocks((state) => [createNewBlock(), ...state]);
  };

  const updateBlockCaption = (blockKey: string, caption: string) => {
    setBlocks((state) =>
      state.map((block) =>
        block.key === blockKey ? { ...block, caption } : block
      )
    );
  };

  const updateDraft = (
    blockKey: string,
    draftKey: string,
    patch: Partial<DraftImage>
  ) => {
    setBlocks((state) =>
      state.map((block) => {
        if (block.key !== blockKey) {
          return block;
        }

        return {
          ...block,
          drafts: block.drafts.map((draft) =>
            draft.key === draftKey ? { ...draft, ...patch } : draft
          ),
        };
      })
    );
  };

  const addDraftImage = (blockKey: string) => {
    setBlocks((state) =>
      state.map((block) =>
        block.key === blockKey
          ? { ...block, drafts: [...block.drafts, createDraftImage()] }
          : block
      )
    );
  };

  const removeDraftImage = (blockKey: string, draftKey: string) => {
    setBlocks((state) =>
      state.map((block) =>
        block.key === blockKey
          ? {
              ...block,
              drafts: block.drafts.filter((draft) => draft.key !== draftKey),
            }
          : block
      )
    );
  };

  const removeMaterial = async (id: number) => {
    setRemovingIds((state) => [...state, id]);
    try {
      await axAPI.post(`/cms/medicine/materials/remove/${id}`);
      message.success("Материал удален");
      await loadMaterials();
    } catch (error: any) {
      message.error(error?.message || "Не удалось удалить материал");
    } finally {
      setRemovingIds((state) => state.filter((item) => item !== id));
    }
  };

  const removeBlock = async (block: MaterialBlock) => {
    const ids = block.materials.map((material) => material.id);

    if (!ids.length) {
      setBlocks((state) => state.filter((item) => item.key !== block.key));
      return;
    }

    setRemovingIds((state) => [...state, ...ids]);
    try {
      await Promise.all(
        ids.map((id) => axAPI.post(`/cms/medicine/materials/remove/${id}`))
      );
      message.success("Блок удален");
      await loadMaterials();
    } catch (error: any) {
      message.error(error?.message || "Не удалось удалить блок");
    } finally {
      setRemovingIds((state) => state.filter((id) => !ids.includes(id)));
    }
  };

  const saveBlock = async (block: MaterialBlock) => {
    const caption = block.caption.trim();
    const drafts = getFilledDrafts(block.drafts);

    if (!caption) {
      message.error("Введите заголовок блока");
      return;
    }

    if (!drafts.length) {
      message.error("Добавьте хотя бы одну картинку");
      return;
    }

    if (drafts.some((draft) => !draft.imageUrl || !draft.detailImage)) {
      message.error(
        "Для каждой картинки укажите превью и детальное изображение"
      );
      return;
    }

    setSavingBlockKey(block.key);
    try {
      await Promise.all(
        drafts.map((draft) =>
          axAPI.post("/cms/medicine/materials/upsert", {
            caption,
            imageUrl: draft.imageUrl,
            detailImage: draft.detailImage,
          })
        )
      );
      message.success("Материалы сохранены");
      await loadMaterials();
    } catch (error: any) {
      message.error(error?.message || "Не удалось сохранить материалы");
    } finally {
      setSavingBlockKey(undefined);
    }
  };

  return {
    blocks,
    loading,
    removingIds,
    savingBlockKey,
    addBlock,
    addDraftImage,
    removeBlock,
    removeDraftImage,
    removeMaterial,
    saveBlock,
    updateBlockCaption,
    updateDraft,
  };
};
